import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { CheckCircle, Shield, Zap, TrendingUp, Users, Globe, Monitor, FileCheck } from "lucide-react";

interface HomepageProps {
  setCurrentPage: (page: string) => void;
}

export function Homepage({ setCurrentPage }: HomepageProps) {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#e6f2ff] via-white to-gray-50 pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="text-lg md:text-xl text-[#004aad] mb-4 tracking-wide">
              MusliFin, Your Muslim Financing Partner
            </div>
            <h1 className="text-4xl md:text-6xl mb-6 text-gray-900">
              The Gateway for Shariah-Compliant Auto Finance Across North America
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Enabling Muslims to complete their entire car buying and financing journey online through our Sayaara Platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => setCurrentPage('dealerships')}
                className="bg-[#004aad] hover:bg-[#003a8a] text-white px-8 py-4 text-lg"
              >
                Partner With Us
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={() => setCurrentPage('investors')}
                className="border-[#004aad] text-[#004aad] hover:bg-[#e6f2ff] px-8 py-4 text-lg"
              >
                Request Investor Deck
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">How MusliFin Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A seamless blend of online convenience and in-person trust verification
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-6">
                <Monitor className="w-8 h-8 text-[#004aad]" />
              </div>
              <h3 className="text-xl mb-4 text-gray-900">Online Journey</h3>
              <p className="text-gray-600">
                Customers browse, apply, and get pre-approved entirely online through our Sayaara platform. No dealership overhead required.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-6">
                <FileCheck className="w-8 h-8 text-[#004aad]" />
              </div>
              <h3 className="text-xl mb-4 text-gray-900">In-Person Verification</h3>
              <p className="text-gray-600">
                Final contract signing and ID verification happens in-person at the dealership, ensuring legal compliance and building trust.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-[#004aad]" />
              </div>
              <h3 className="text-xl mb-4 text-gray-900">Future: MusliServe</h3>
              <p className="text-gray-600">
                Coming 2026: Automated payment collection and compliance management through our MusliServe platform.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">
                Millions of Muslims in North America are excluded from traditional finance due to faith-based restrictions.
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                MusliFin changes that.
              </p>
              <p className="text-lg text-gray-600">
                We're building the infrastructure for ethical, Shariah-compliant financial products that serve the growing Muslim market while creating new opportunities for dealerships and investors.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-3xl text-[#004aad] mb-2">5M+</div>
                  <div className="text-gray-600">Muslims in North America</div>
                </div>
                <div>
                  <div className="text-3xl text-[#004aad] mb-2">$30B+</div>
                  <div className="text-gray-600">Auto finance opportunity</div>
                </div>
                <div>
                  <div className="text-3xl text-[#004aad] mb-2">0</div>
                  <div className="text-gray-600">Trusted providers</div>
                </div>
                <div>
                  <div className="text-3xl text-[#004aad] mb-2">100%</div>
                  <div className="text-gray-600">Shariah compliant</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">What We Offer</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive platforms serving dealerships and investors in the ethical finance ecosystem.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Sayaara Card */}
            <Card className="p-8 border-2 border-[#ccdfff] hover:border-[#99c7ff] transition-colors">
              <div className="text-center mb-6">
                <h3 className="text-2xl mb-4 text-gray-900">Sayaara Platform</h3>
                <p className="text-lg text-gray-600 mb-6">Complete Online Auto Finance Journey</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">Online browsing and applications</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Shield className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">Shariah-Compliant contracts</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Users className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">Pre-screened credit approval</span>
                </div>
                <div className="flex items-center space-x-3">
                  <FileCheck className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">In-person contract signing</span>
                </div>
              </div>
            </Card>

            {/* Investors Card */}
            <Card className="p-8 border-2 border-[#ccdfff] hover:border-[#99c7ff] transition-colors">
              <div className="text-center mb-6">
                <h3 className="text-2xl mb-4 text-gray-900">For Investors</h3>
                <p className="text-lg text-gray-600 mb-6">Ethical Investment Opportunities</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <TrendingUp className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">Halal investment returns</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Shield className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">Asset-backed securities</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Globe className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">MusliInvest platform (Coming 2026)</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Users className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">Capital recycling opportunities</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">Why Work With Us</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8 text-[#004aad]" />
              </div>
              <h3 className="text-xl mb-4 text-gray-900">First-Mover Advantage</h3>
              <p className="text-gray-600">
                Be the first in your market to offer Shariah-compliant auto financing and capture an underserved customer base.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-[#004aad]" />
              </div>
              <h3 className="text-xl mb-4 text-gray-900">Legal + Shariah Compliance</h3>
              <p className="text-gray-600">
                Our products are vetted by both legal experts and Islamic scholars to ensure full compliance.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-[#004aad]" />
              </div>
              <h3 className="text-xl mb-4 text-gray-900">Zero Overhead Solution</h3>
              <p className="text-gray-600">
                Complete online customer journey with automated backend. Dealerships focus on what they do best.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-[#004aad]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl mb-6 text-white">
            Join North America's Trusted Platform for Halal Auto Finance
          </h2>
          <p className="text-xl text-[#ccdfff] mb-8">
            Whether you're a dealership looking to serve new customers or an investor seeking ethical opportunities, we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => setCurrentPage('dealerships')}
              className="bg-white text-[#004aad] hover:bg-gray-100 px-8 py-4 text-lg"
            >
              Become a Partner Dealership
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => setCurrentPage('investors')}
              className="border-white !text-white hover:bg-[#0056cc] hover:border-[#0056cc] !bg-transparent px-8 py-4 text-lg"
            >
              Explore Investment Opportunities
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}